
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import ContentSwitcher from '../../../components/docs/ContentSwitcher';
import SampleCode from '../../../components/docs/plaidoms/SampleCode';
import Actions from '../../../components/docs/Actions';
export const metadata = {
  toc: true,
  layout: 'guide',
  description: "Learn how to add Plaid Layer to your application to provide a fast, frictionless onboarding experience"
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Plaid Layer integration guide' subtitle='Use Plaid Layer to instantly onboard new customers' mdxType="Header" />
    <p>{`In this guide, we'll start from scratch and walk through how to use `}<a parentName="p" {...{
        "href": "/docs/api/products/layer/"
      }}>{`Plaid Layer`}</a>{` to create a fast, frictionless onboarding experience for your customers.`}</p>
    <p>{`If you're already familiar with Link, you can skip to `}<a parentName="p" {...{
        "href": "#create-a-link-token"
      }}>{`Create a Link token`}</a>{`. `}</p>
    <h4 {...{
      "id": "get-plaid-api-keys-and-complete-application-and-company-profile"
    }}>{`Get Plaid API keys and complete application and company profile`}</h4>
    <p>{`If you don't already have one, you'll need to `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/signup"
      }}>{`create a Plaid developer account`}</a>{`. After creating your account, you can find your `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/keys"
      }}>{`API keys`}</a>{` under Build in the Plaid Dashboard.`}</p>
    <p>{`You will also need to complete your `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/settings/company/app-branding"
      }}>{`application profile`}</a>{` and `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/settings/company/profile"
      }}>{`company profile`}</a>{` in the Dashboard. The information in your profile will be shared with users of your application when they manage their connection on the `}<a parentName="p" {...{
        "href": "https://my.plaid.com"
      }}>{`Plaid Portal`}</a>{`.`}</p>
    <h4 {...{
      "id": "install-and-initialize-plaid-libraries"
    }}>{`Install and initialize Plaid libraries`}</h4>
    <p>{`You can use our official server-side client libraries to connect to the Plaid API from your application:`}</p>
    <SampleCode.InstallClientLibraries />
    <p>{`After you've installed Plaid's client libraries, you can initialize them by passing in your `}<inlineCode parentName="p">{`client_id`}</inlineCode>{`, `}<inlineCode parentName="p">{`secret`}</inlineCode>{`, and the environment you wish to connect to (Sandbox or Production). This will make sure the client libraries pass along your `}<inlineCode parentName="p">{`client_id`}</inlineCode>{` and `}<inlineCode parentName="p">{`secret`}</inlineCode>{` with each request, and you won't need to explicitly include them in any other calls.`}</p>
    <SampleCode.InitializeClientLibraries />
    <h4 {...{
      "id": "launch-link"
    }}>{`Launch Link`}</h4>
    <p>{`Plaid Link is a drop-in module that provides a secure, elegant flow for Layer.  Before initializing Link, you will need to create a new `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` on the server side of your application. A `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` is a short-lived, one-time use token that is used to authenticate your app with Link.
You can create one using the `}<inlineCode parentName="p">{`/session/token/create`}</inlineCode>{` endpoint. Then, on the client side of your application, you'll need to initialize Link with the `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` that you just created.`}</p>
    <h5 {...{
      "id": "create-a-link-token"
    }}>{`Create a Link token`}</h5>
    <p>{`Unlike a regular Link flow, the starting point for Layer is a call to `}<inlineCode parentName="p">{`/session/token/create`}</inlineCode>{` with a specified `}<inlineCode parentName="p">{`TEMPLATE_ID`}</inlineCode>{`. The template should be created and configured in the dashboard ahead of time. If you want to use other products in addition to the `}<inlineCode parentName="p">{`layer`}</inlineCode>{` product, make sure your template has them enabled.`}</p>
    <SampleCode.CreateSessionToken />
    <h5 {...{
      "id": "install-the-link-sdks"
    }}>{`Install the Link SDKs`}</h5>
    <p>{`For instructions on installing Link SDKs, see the `}<a parentName="p" {...{
        "href": "/docs/link"
      }}>{`Link documentation`}</a>{` for your platform: `}<a parentName="p" {...{
        "href": "/docs/link/ios"
      }}>{`iOS`}</a>{`, `}<a parentName="p" {...{
        "href": "/docs/link/android"
      }}>{`Android`}</a>{`, `}<a parentName="p" {...{
        "href": "/docs/link/react-native"
      }}>{`React Native`}</a>{`, `}<a parentName="p" {...{
        "href": "/docs/link/flutter"
      }}>{`Flutter`}</a>{`, or `}<a parentName="p" {...{
        "href": "/docs/link/web"
      }}>{`React`}</a>{`. Layer is not supported on mobile webview integrations.`}</p>
    <p>{`As Plaid is actively adding new Layer functionality, it is strongly recommended that you use the latest SDK version if your app uses Layer. `}</p>
    <p>{`If you are developing a native mobile application, minimum SDK versions for Layer are 6.0.4 (iOS), 4.5.0 (Android), 11.11.0 (React Native), 1.0.0 (Flutter), and 3.5.2 (React). For Extended Autofill support, minimum versions are 6.3.1 (iOS), 5.3.0 (Android), 12.4.0 (React Native), 1.0.0 (Flutter), and 4.1.1 (React).`}</p>
    <p>{`Layer is not compatible with the `}<a parentName="p" {...{
        "href": "/docs/link/hosted-link/"
      }}>{`Hosted Link`}</a>{` integration mode.`}</p>
    <h5 {...{
      "id": "create-the-link-handler-or-session"
    }}>{`Create the Link handler or session`}</h5>
    <p>{`Basic sample code for creating the Link handler or session is shown below. For more details, see the `}<a parentName="p" {...{
        "href": "/docs/link"
      }}>{`Link documentation`}</a>{` for your platform: `}<a parentName="p" {...{
        "href": "/docs/link/ios"
      }}>{`iOS`}</a>{`, `}<a parentName="p" {...{
        "href": "/docs/link/android"
      }}>{`Android`}</a>{`, `}<a parentName="p" {...{
        "href": "/docs/link/react-native"
      }}>{`React Native`}</a>{`, `}<a parentName="p" {...{
        "href": "/docs/link/flutter"
      }}>{`Flutter`}</a>{`, or `}<a parentName="p" {...{
        "href": "/docs/link/web"
      }}>{`React`}</a>{`.`}</p>
    <Callout mdxType="Callout">
      <p>{`Ensure you are creating a Link token and passing it to the Link SDK as early as possible. The more time between when you create your handler or session and when you open Link, the better the UX will be for your users.`}</p>
    </Callout>
    <Callout mdxType="Callout">
      <p>{`If you already have an existing Android, React Native, or Flutter integration, make sure you are on a Layer-capable SDK version (see the minimums above) and are using the current session-based APIs to open Link. For more details, see `}<a parentName="p" {...{
          "href": "/docs/link/react-native/#opening-link"
        }}>{`React Native: opening Link`}</a>{`, `}<a parentName="p" {...{
          "href": "/docs/link/flutter/#opening-link"
        }}>{`Flutter: opening Link`}</a>{`, and `}<a parentName="p" {...{
          "href": "/docs/link/android/#opening-link"
        }}>{`Android: opening Link`}</a>{`.`}</p>
    </Callout>
    <ContentSwitcher.Container id='configure' names={['Android', 'iOS', 'React Native', 'Flutter', 'Web']} hiddenIsSearchable={true}>
      <ContentSwitcher.Content>
        <h5 {...{
          "id": "create-a-layertokenconfiguration"
        }}>{`Create a LayerTokenConfiguration`}</h5>
        <p>{`Each time you open Link, you will need to get a new `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` from your server and create a new
`}<inlineCode parentName="p">{`LayerTokenConfiguration`}</inlineCode>{` object with it.`}</p>
        <MDXCodeBlock type='android' mdxType="MDXCodeBlock">
          <pre><code parentName="pre" {...{
              "className": "language-kotlin"
            }}>{`val layerTokenConfiguration = LayerTokenConfiguration.Builder()
    .token("LINK_TOKEN_FROM_SERVER")
    .build()
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-java"
            }}>{`LayerTokenConfiguration layerTokenConfiguration = new LayerTokenConfiguration.Builder()
    .token("LINK_TOKEN_FROM_SERVER")
    .build();
`}</code></pre>
        </MDXCodeBlock>
        <p>{`The Link SDK runs as a separate `}<inlineCode parentName="p">{`Activity`}</inlineCode>{` within your app. In order to return the result
to your app, it supports both the standard `}<inlineCode parentName="p">{`startActivityForResult`}</inlineCode>{` and `}<inlineCode parentName="p">{`onActivityResult`}</inlineCode>{`
and the `}<inlineCode parentName="p">{`ActivityResultContract`}</inlineCode>{` `}<a parentName="p" {...{
            "href": "https://developer.android.com/training/basics/intents/result"
          }}>{`result APIs`}</a>{`.`}</p>
        <ContentSwitcher.Container id='Link Result Handling' names={["Activity Result APIs", "Standard Activity APIs"]}>
          <ContentSwitcher.Content>
            <h5 {...{
              "id": "register-a-callback-for-an-activity-result"
            }}>{`Register a callback for an Activity Result`}</h5>
            <MDXCodeBlock type="android" mdxType="MDXCodeBlock">
              <pre><code parentName="pre" {...{
                  "className": "language-kotlin"
                }}>{`private val linkAccountToPlaid =
registerForActivityResult(OpenPlaidLink()) { result ->
  when (result) {
    is LinkSuccess -> /* handle LinkSuccess */
    is LinkExit -> /* handle LinkExit */
  }
}
`}</code></pre>
              <pre><code parentName="pre" {...{
                  "className": "language-java"
                }}>{`private ActivityResultLauncher<PlaidSession> linkAccountToPlaid = registerForActivityResult(new OpenPlaidLink(),
  result -> {
    if (result instanceof LinkSuccess) {
      /* handle LinkSuccess */
    } else {
      /* handle LinkExit */
    }
  }
);
`}</code></pre>
            </MDXCodeBlock>
            <h5 {...{
              "id": "create-a-plaidlayersession"
            }}>{`Create a PlaidLayerSession`}</h5>
            <MDXCodeBlock type="android" mdxType="MDXCodeBlock">
              <pre><code parentName="pre" {...{
                  "className": "language-kotlin"
                }}>{`val layerSession: PlaidLayerSession =
  Plaid.createPlaidLayerSession(this, layerTokenConfiguration)
`}</code></pre>
              <pre><code parentName="pre" {...{
                  "className": "language-java"
                }}>{`layerSession = Plaid.createPlaidLayerSession(this, layerTokenConfiguration);
`}</code></pre>
            </MDXCodeBlock>
            <h5 {...{
              "id": "open-link"
            }}>{`Open Link`}</h5>
            <MDXCodeBlock type="android" mdxType="MDXCodeBlock">
              <pre><code parentName="pre" {...{
                  "className": "language-kotlin"
                }}>{`linkAccountToPlaid.launch(layerSession)
`}</code></pre>
              <pre><code parentName="pre" {...{
                  "className": "language-java"
                }}>{`linkAccountToPlaid.launch(layerSession);
`}</code></pre>
            </MDXCodeBlock>
          </ContentSwitcher.Content>
          <ContentSwitcher.Content>
            <h5 {...{
              "id": "set-up-parsing-in-onactivityresult"
            }}>{`Set up parsing in onActivityResult`}</h5>
            <p>{`Call `}<inlineCode parentName="p">{`Plaid.parseResult`}</inlineCode>{` in your Activity's `}<inlineCode parentName="p">{`onActivityResult`}</inlineCode>{` to parse the result. It returns a
`}<inlineCode parentName="p">{`LinkResult`}</inlineCode>{` if the result was sent from Link, or `}<inlineCode parentName="p">{`null`}</inlineCode>{` otherwise.`}</p>
            <MDXCodeBlock type="android" mdxType="MDXCodeBlock">
              <pre><code parentName="pre" {...{
                  "className": "language-kotlin"
                }}>{`override fun onActivityResult(requestCode: Int, resultCode: Int, data: Intent?) {
  super.onActivityResult(requestCode, resultCode, data)
  Plaid.parseResult(requestCode, resultCode, data)?.let { result ->
    when (result) {
      is LinkSuccess -> /* handle LinkSuccess */
      is LinkExit -> /* handle LinkExit */
    }
  }
}
`}</code></pre>
              <pre><code parentName="pre" {...{
                  "className": "language-java"
                }}>{`@Override
protected void onActivityResult(int requestCode, int resultCode, @Nullable Intent data) {
  super.onActivityResult(requestCode, resultCode, data);
  LinkResult result = Plaid.parseResult(requestCode, resultCode, data);
  if (result instanceof LinkSuccess) {
    /* handle LinkSuccess */
  } else if (result instanceof LinkExit) {
    /* handle LinkExit */
  }
}
`}</code></pre>
            </MDXCodeBlock>
            <h5 {...{
              "id": "create-a-plaidlayersession-1"
            }}>{`Create a PlaidLayerSession`}</h5>
            <MDXCodeBlock type='android' mdxType="MDXCodeBlock">
              <pre><code parentName="pre" {...{
                  "className": "language-kotlin"
                }}>{`val layerSession: PlaidLayerSession = Plaid.createPlaidLayerSession(this, layerTokenConfiguration)
`}</code></pre>
              <pre><code parentName="pre" {...{
                  "className": "language-java"
                }}>{`PlaidLayerSession layerSession = Plaid.createPlaidLayerSession(this, layerTokenConfiguration);
`}</code></pre>
            </MDXCodeBlock>
          </ContentSwitcher.Content>
        </ContentSwitcher.Container>
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <h5 {...{
          "id": "create-a-configuration"
        }}>{`Create a Configuration`}</h5>
        <p>{`Once the Link token is passed to your app, create a configuration object.`}</p>
        <ContentSwitcher.Container id='layer-ios-configuration' names={['Swift', 'Objective-C']} hiddenIsSearchable={true}>
          <ContentSwitcher.Content>
            <pre><code parentName="pre" {...{
                "className": "language-swift",
                "metastring": "switcher=false #Swift",
                "switcher": "false",
                "#Swift": true
              }}>{`let configuration = LayerTokenConfiguration(
    token: "<#LINK_TOKEN_FROM_SERVER#>",
    onSuccess: { success in
        // Send success.publicToken to your app server.
    },
    onExit: { exit in
        // Optionally handle exit data.
    },
    onEvent: { event in
        // Optionally handle Link events.
    }
)
`}</code></pre>
          </ContentSwitcher.Content>
          <ContentSwitcher.Content>
            <pre><code parentName="pre" {...{
                "className": "language-objectivec",
                "metastring": "switcher=false #Objective-C",
                "switcher": "false",
                "#Objective-C": true
              }}>{`PLKLayerTokenConfiguration* layerConfiguration = [PLKLayerTokenConfiguration createWithToken:@"<#LINK_TOKEN_FROM_SERVER#>"
                                                                                   onSuccess:^(PLKLinkSuccess *linkSuccess) {
  // Send the linkSuccess.publicToken to your app server.
}];
`}</code></pre>
          </ContentSwitcher.Content>
        </ContentSwitcher.Container>
        <h5 {...{
          "id": "create-a-handler-or-session"
        }}>{`Create a Handler or Session`}</h5>
        <p>{`In Swift, create and retain a `}<inlineCode parentName="p">{`PlaidLayerSession`}</inlineCode>{` for the duration of the Plaid SDK flow. In Objective-C, create and retain a `}<inlineCode parentName="p">{`PLKPlaidLayerSession`}</inlineCode>{`.`}</p>
        <ContentSwitcher.Container id='layer-ios-handler-or-session' names={['Swift', 'Objective-C']} hiddenIsSearchable={true}>
          <ContentSwitcher.Content>
            <pre><code parentName="pre" {...{
                "className": "language-swift",
                "metastring": "switcher=false #Swift",
                "switcher": "false",
                "#Swift": true
              }}>{`private var layerSession: PlaidLayerSession?

do {
    layerSession = try Plaid.createPlaidLayerSession(configuration: configuration)
} catch {
    // Handle the error.
}
`}</code></pre>
          </ContentSwitcher.Content>
          <ContentSwitcher.Content>
            <pre><code parentName="pre" {...{
                "className": "language-objectivec",
                "metastring": "switcher=false #Objective-C",
                "switcher": "false",
                "#Objective-C": true
              }}>{`NSError *createError = nil;
id<PLKPlaidLayerSession> layerSession = [PLKPlaid createPlaidLayerSessionWithConfiguration:layerConfiguration
                                                                                    error:&createError];
if (layerSession) {
    self.layerSession = layerSession;
} else if (createError) {
    NSLog(@"Unable to create PLKPlaidLayerSession due to: %@", createError);
}
`}</code></pre>
          </ContentSwitcher.Content>
        </ContentSwitcher.Container>
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <p>{`Create a Layer session by calling `}<inlineCode parentName="p">{`createPlaidLayerSession`}</inlineCode>{` with your `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` and callbacks. Open Link later, once the session emits the `}<inlineCode parentName="p">{`LAYER_READY`}</inlineCode>{` event.`}</p>
        <pre><code parentName="pre" {...{
            "className": "language-javascript",
            "metastring": "switcher=false #Create a Layer session",
            "switcher": "false",
            "#Create": true,
            "a": true,
            "Layer": true,
            "session": true
          }}>{`const session = await createPlaidLayerSession({
  token: linkToken,
  onSuccess: (success: LinkSuccess) => {
    console.log(success);
  },
  onExit: (exit: LinkExit) => {
    console.log(exit);
  },
  onEvent: (event: LinkEvent) => {
    // Open Link when event.eventName === LinkEventName.LAYER_READY
  },
});
`}</code></pre>
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <p>{`Create a Layer session by calling `}<inlineCode parentName="p">{`createPlaidLayerSession`}</inlineCode>{` with your `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` and callbacks. Open Link later, once the session emits the `}<inlineCode parentName="p">{`LAYER_READY`}</inlineCode>{` event.`}</p>
        <pre><code parentName="pre" {...{
            "className": "language-dart",
            "metastring": "switcher=false #Create a Layer session",
            "switcher": "false",
            "#Create": true,
            "a": true,
            "Layer": true,
            "session": true
          }}>{`PlaidLayerSession? _layerSession;

Future<void> createLayer(String linkToken) async {
  _layerSession = await createPlaidLayerSession(
    LayerTokenConfiguration(
      token: linkToken,
      onSuccess: (success) {
        // Send success.publicToken to your app server.
      },
      onExit: (exit) {
        // Optionally handle exit data.
      },
      onEvent: (event) {
        // Open Link when event.eventName == LinkEventName.layerReady.
      },
    ),
  );
}
`}</code></pre>
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <p>{`Call `}<inlineCode parentName="p">{`Plaid.create`}</inlineCode>{` (or, if using React, `}<inlineCode parentName="p">{`usePlaidLink`}</inlineCode>{`) when initializing the view that is responsible for loading Plaid.`}</p>
        <MDXCodeBlock type='frontend' title="Create example" mdxType="MDXCodeBlock">
          <pre><code parentName="pre" {...{
              "className": "language-javascript"
            }}>{`const handler = Plaid.create({
  token: 'GENERATED_LINK_TOKEN',
  onSuccess: (public_token, metadata) => {},
  onLoad: () => {},
  onExit: (err, metadata) => {},
  onEvent: (eventName, metadata) => {},
});
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-tsx"
            }}>{`// The usePlaidLink hook manages Plaid Link creation
// It does not return a destroy function;
// instead, on unmount it automatically destroys the Link instance
const config: PlaidLinkOptions = {
  onSuccess: (public_token, metadata) => {},
  onExit: (err, metadata) => {},
  onEvent: (eventName, metadata) => {},
  token: 'GENERATED_LINK_TOKEN',
};

const { open, exit, ready, submit } = usePlaidLink(config);
`}</code></pre>
        </MDXCodeBlock>
      </ContentSwitcher.Content>
    </ContentSwitcher.Container>
    <h4 {...{
      "id": "submit-the-users-phone-number"
    }}>{`Submit the user's phone number`}</h4>
    <p>{`Call the `}<inlineCode parentName="p">{`submit`}</inlineCode>{` method on the handler or session you created earlier with the user's phone number. The semantics depend on the language/platform, but all methods are called `}<inlineCode parentName="p">{`submit`}</inlineCode>{`.`}</p>
    <p>{`In Sandbox, submit the phone number of a `}<a parentName="p" {...{
        "href": "/docs/sandbox/profiles/"
      }}>{`Sandbox Profile`}</a>{` and use the OTP `}<inlineCode parentName="p">{`123456`}</inlineCode>{`. See `}<a parentName="p" {...{
        "href": "/docs/layer/#testing-layer-in-sandbox"
      }}>{`Testing Layer`}</a>{`.`}</p>
    <ContentSwitcher.Container id='submit' names={['Android', 'iOS', 'React Native', 'Flutter', 'Web']} hiddenIsSearchable={true}>
      <ContentSwitcher.Content>
        <MDXCodeBlock type="android" mdxType="MDXCodeBlock">
          <pre><code parentName="pre" {...{
              "className": "language-kotlin"
            }}>{`val submissionData = SubmissionData(phoneNumber = phoneNumber)
layerSession.submit(submissionData)
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-java"
            }}>{`SubmissionData submissionData = new SubmissionData(phoneNumber, null, null);
layerSession.submit(submissionData);
`}</code></pre>
        </MDXCodeBlock>
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <pre><code parentName="pre" {...{
            "className": "language-swift",
            "metastring": "switcher=false",
            "switcher": "false"
          }}>{`// Create a model that conforms to the SubmissionData interface
struct PlaidSubmitData: SubmissionData {
    var phoneNumber: String?
    var dateOfBirth: String?
    var params: [String: String]?
}

let data = PlaidSubmitData(phoneNumber: "+14155550015")

layerSession?.submit(data: data)
`}</code></pre>
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <pre><code parentName="pre" {...{
            "className": "language-javascript",
            "metastring": "switcher=false",
            "switcher": "false"
          }}>{`await session.submit({
  phoneNumber: '+14155550123',
});
`}</code></pre>
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <pre><code parentName="pre" {...{
            "className": "language-dart",
            "metastring": "switcher=false",
            "switcher": "false"
          }}>{`await _layerSession?.submit(
  const SubmissionData(phoneNumber: '+14155550123'),
);
`}</code></pre>
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <pre><code parentName="pre" {...{
            "className": "language-javascript",
            "metastring": "switcher=false",
            "switcher": "false"
          }}>{`handler.submit({
  "phone_number": "+14155550123"
})
`}</code></pre>
      </ContentSwitcher.Content>
    </ContentSwitcher.Container>
    <h4 {...{
      "id": "submit-the-users-date-of-birth"
    }}>{`Submit the user's date of birth`}</h4>
    <p>{`Upon receiving `}<inlineCode parentName="p">{`LAYER_NOT_AVAILABLE`}</inlineCode>{` after phone number submission, collect the user's date of birth and call the `}<inlineCode parentName="p">{`submit`}</inlineCode>{` method on the existing Plaid handler. For more details, see `}<a parentName="p" {...{
        "href": "/docs/layer/#extended-autofill"
      }}>{`Extended Autofill`}</a>{`. `}</p>
    <p>{`This functionality requires minimum SDK versions 6.3.1 (iOS), 5.3.0 (Android), 12.4.0 (React Native), 1.0.0 (Flutter), and 4.1.1 (React). If you are using an older SDK version or do not wish to use Extended Autofill, you can skip this step.`}</p>
    <ContentSwitcher.Container id='submit' names={['Android', 'iOS', 'React Native', 'Flutter', 'Web']} hiddenIsSearchable={true}>
      <ContentSwitcher.Content>
        <MDXCodeBlock type="android" mdxType="MDXCodeBlock">
          <pre><code parentName="pre" {...{
              "className": "language-kotlin"
            }}>{`val submissionData = SubmissionData(dateOfBirth = "1975-01-18")
layerSession.submit(submissionData)
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-java"
            }}>{`SubmissionData submissionData = new SubmissionData(null, "1975-01-18", null);
layerSession.submit(submissionData);
`}</code></pre>
        </MDXCodeBlock>
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <pre><code parentName="pre" {...{
            "className": "language-swift",
            "metastring": "switcher=false",
            "switcher": "false"
          }}>{`// Create a model that conforms to the SubmissionData interface
struct PlaidSubmitData: SubmissionData {
    var dateOfBirth: String?
    var phoneNumber: String?
    var params: [String: String]?
}

let data = PlaidSubmitData(dateOfBirth: "1975-01-18")

layerSession?.submit(data: data)
`}</code></pre>
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <pre><code parentName="pre" {...{
            "className": "language-javascript",
            "metastring": "switcher=false",
            "switcher": "false"
          }}>{`await session.submit({
  dateOfBirth: '1975-01-18',
});
`}</code></pre>
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <pre><code parentName="pre" {...{
            "className": "language-dart",
            "metastring": "switcher=false",
            "switcher": "false"
          }}>{`await _layerSession?.submit(
  const SubmissionData(dateOfBirth: '1975-01-18'),
);
`}</code></pre>
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <pre><code parentName="pre" {...{
            "className": "language-javascript",
            "metastring": "switcher=false",
            "switcher": "false"
          }}>{`handler.submit({
  "date_of_birth": "1975-01-18"
})
`}</code></pre>
      </ContentSwitcher.Content>
    </ContentSwitcher.Container>
    <h4 {...{
      "id": "open-layer-ui-on-the-layer_ready-event"
    }}>{`Open Layer UI on the LAYER_READY event`}</h4>
    <p>{`Listen to the events on the handler or session via `}<inlineCode parentName="p">{`onEvent`}</inlineCode>{`. For platform-specific details, see the `}<a parentName="p" {...{
        "href": "/docs/link"
      }}>{`Link documentation`}</a>{` for your platform.`}</p>
    <p>{`If you receive `}<inlineCode parentName="p">{`LAYER_READY`}</inlineCode>{`, the user is eligible for the Layer flow and you should proceed to open Link according to the `}<a parentName="p" {...{
        "href": "/docs/link"
      }}>{`Link documentation`}</a>{` for your platform.`}</p>
    <p>{`If you receive `}<inlineCode parentName="p">{`LAYER_AUTOFILL_NOT_AVAILABLE`}</inlineCode>{` (or if you receive `}<inlineCode parentName="p">{`LAYER_NOT_AVAILABLE`}</inlineCode>{` and have not built Extended Autofill support for your integration), the user is not eligible for the Layer flow. You can clean up the handler you created earlier and fall back to whatever non-Layer onboarding flow fits your application (e.g. a traditional Link session, or other custom flow for your app).`}</p>
    <ContentSwitcher.Container id='submit' names={['Android', 'iOS', 'React Native', 'Flutter', 'Web']} hiddenIsSearchable={true}>
      <ContentSwitcher.Content>
        <MDXCodeBlock type="android" mdxType="MDXCodeBlock">
          <pre><code parentName="pre" {...{
              "className": "language-kotlin"
            }}>{`Plaid.setLinkEventListener { event ->
    when(event.eventName) {
        is LinkEventName.LAYER_READY -> {
            // open link
            linkAccountToPlaid.launch(layerSession)
        }
        is LinkEventName.LAYER_NOT_AVAILABLE -> {
            // run fall back flow
        }
        else -> { Log.i("Event", event.toString()) }
    }
}
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-java"
            }}>{`Plaid.setLinkEventListener(new LinkEventListener() {
    @Override
    public void onEvent(LinkEvent event) {
        LinkEventName eventName = event.getEventName();
        if (eventName instanceof LinkEventName.LAYER_READY) {
            // Open link
            linkAccountToPlaid.launch(layerSession);
        } else if (eventName instanceof LinkEventName.LAYER_NOT_AVAILABLE) {
            // Run fallback flow
        } else {
            Log.i("Event", event.toString());
        }
    }
});
`}</code></pre>
        </MDXCodeBlock>
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <pre><code parentName="pre" {...{
            "className": "language-swift",
            "metastring": "switcher=false",
            "switcher": "false"
          }}>{`let configuration = LayerTokenConfiguration(
    token: "<#LINK_TOKEN_FROM_SERVER#>",
    onSuccess: { success in
        // Send success.publicToken to your app server.
    },
    onExit: { exit in
        // Optionally handle exit data.
    },
    onEvent: { [weak self] event in
        guard let self = self else { return }
        switch event.eventName {
        case .layerReady:
            self.layerSession?.open(using: .viewController(self))
        case .layerNotAvailable:
            // Fall back on non-Layer flows, clean up
            break
        default:
            // Other cases ignored in this use case.
            break
        }
    }
)
`}</code></pre>
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <pre><code parentName="pre" {...{
            "className": "language-javascript",
            "metastring": "switcher=false #JavaScript sample code",
            "switcher": "false",
            "#JavaScript": true,
            "sample": true,
            "code": true
          }}>{`import {
  LinkEvent,
  LinkEventName,
  LinkExit,
  LinkSuccess,
  PlaidLayerSession,
  createPlaidLayerSession,
} from 'react-native-plaid-link-sdk';

let layerSession: PlaidLayerSession;

layerSession = await createPlaidLayerSession({
  token: linkToken,
  onSuccess: (success: LinkSuccess) => {
    console.log(success);
  },
  onExit: (exit: LinkExit) => {
    console.log(exit);
  },
  onEvent: async (event: LinkEvent) => {
    switch (event.eventName) {
      case LinkEventName.LAYER_READY:
        await layerSession.open();
        break;
      case LinkEventName.LAYER_NOT_AVAILABLE:
        // Run another fallback flow.
        break;
      default:
        break;
    }
  },
});
`}</code></pre>
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <pre><code parentName="pre" {...{
            "className": "language-dart",
            "metastring": "switcher=false #Dart sample code",
            "switcher": "false",
            "#Dart": true,
            "sample": true,
            "code": true
          }}>{`import 'package:plaid_link_flutter/plaid_link_flutter.dart';

PlaidLayerSession? _layerSession;

Future<void> createLayer(String linkToken) async {
  _layerSession = await createPlaidLayerSession(
    LayerTokenConfiguration(
      token: linkToken,
      onSuccess: (success) {
        // Send success.publicToken to your app server.
      },
      onExit: (exit) {
        // Optionally handle exit data.
      },
      onEvent: handleLayerEvent,
    ),
  );
}

void handleLayerEvent(LinkEvent event) {
  switch (event.eventName) {
    case LinkEventName.layerReady:
      _layerSession?.open();
      break;
    case LinkEventName.layerNotAvailable:
      // Run another fallback flow.
      break;
    default:
      break;
  }
}
`}</code></pre>
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <pre><code parentName="pre" {...{
            "className": "language-javascript",
            "metastring": "switcher=false",
            "switcher": "false"
          }}>{`//Same onEvent handler from Link create sample
onEvent: (eventName, metadata) => {
  switch(eventName) {
    case "LAYER_READY":
      // Open Link
      open({...})
      break;
  case "LAYER_NOT_AVAILABLE":
    // Run another fallback flow
    break;
  default:
    //Other cases ignored in this use case
    break;
  }
}
`}</code></pre>
      </ContentSwitcher.Content>
    </ContentSwitcher.Container>
    <h4 {...{
      "id": "get-the-public-token-from-the-onsuccess-callback"
    }}>{`Get the public token from the onSuccess callback`}</h4>
    <p>{`On successful completion, an `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback will be invoked, similar to the standard Link flow. Capture the `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` from `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{`.`}</p>
    <ContentSwitcher.Container id='submit' names={['Android', 'iOS', 'React Native', 'Flutter', 'Web']} hiddenIsSearchable={true}>
      <ContentSwitcher.Content>
        <MDXCodeBlock type="android" mdxType="MDXCodeBlock">
          <pre><code parentName="pre" {...{
              "className": "language-kotlin",
              "metastring": "switcher=false #Kotlin sample code",
              "switcher": "false",
              "#Kotlin": true,
              "sample": true,
              "code": true
            }}>{`val profileToken = success.publicToken
// Send the public token to your backend
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-java"
            }}>{`String profileToken = success.getPublicToken();
// Send the public token to your backend
`}</code></pre>
        </MDXCodeBlock>
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <pre><code parentName="pre" {...{
            "className": "language-swift",
            "metastring": "switcher=false",
            "switcher": "false"
          }}>{`onSuccess: { linkSuccess in
}
// Send the public token to your backend
`}</code></pre>
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <pre><code parentName="pre" {...{
            "className": "language-javascript",
            "metastring": "switcher=false",
            "switcher": "false"
          }}>{`const onSuccess = (linkSuccess: LinkSuccess) => {
  let publicToken = linkSuccess.publicToken;
  // Send the public token to your backend
};
`}</code></pre>
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <pre><code parentName="pre" {...{
            "className": "language-dart",
            "metastring": "switcher=false",
            "switcher": "false"
          }}>{`void handleSuccess(LinkSuccess success) {
  final publicToken = success.publicToken;
  // Send the public token to your backend.
}
`}</code></pre>
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <pre><code parentName="pre" {...{
            "className": "language-javascript",
            "metastring": "switcher=false",
            "switcher": "false"
          }}>{`onSuccess: (public_token) => {
  const publicToken = public_token;
  // Send the public token to your backend
};
`}</code></pre>
      </ContentSwitcher.Content>
    </ContentSwitcher.Container>
    <h4 {...{
      "id": "get-user-account-data"
    }}>{`Get user account data`}</h4>
    <p>{`Call `}<inlineCode parentName="p">{`/user_account/session/get`}</inlineCode>{` to retrieve user-permissioned identity information as well as Item access tokens. Unlike typical Plaid Link sessions, where you must first exchange your public token for an access token in order to talk to the Plaid API, the `}<inlineCode parentName="p">{`/user_account/session/get`}</inlineCode>{` endpoint allows you to retrieve user-permissioned identity information as well as Item access tokens in a single call. You can optionally use Plaid products such as `}<a parentName="p" {...{
        "href": "/docs/identity#identity-match"
      }}>{`Identity Match`}</a>{` or `}<a parentName="p" {...{
        "href": "/docs/identity-verification"
      }}>{`Identity Verification`}</a>{` if you wish to verify this data.`}</p>
    <Callout mdxType="Callout">
      <p>{`Because Layer already verifies your user's phone number via OTP or SNA, for a low-friction experience, you should `}<em parentName="p">{`not`}</em>{` perform additional OTP phone number verification as long as Layer has verified the number. `}</p>
      <p>{`The best indication that the number was verified is the `}<a parentName="p" {...{
          "href": "/docs/api/products/layer/#layer_authentication_passed"
        }}><inlineCode parentName="a">{`LAYER_AUTHENTICATION_PASSED`}</inlineCode></a>{` webhook. Before skipping OTP verification based on this webhook, be sure to implement `}<a parentName="p" {...{
          "href": "/docs/api/webhooks/webhook-verification/"
        }}>{`webhook verification`}</a>{` to protect against webhook spoofing attacks.`}</p>
      <p>{`Alternatively, you can use Link completion to indicate that the number was verified, but this method will exclude sessions where the user verified their number without fully completing Link. To use this method, call `}<inlineCode parentName="p">{`/user_account/session/get`}</inlineCode>{`; the `}<inlineCode parentName="p">{`phone_number`}</inlineCode>{` returned is the verified number. Do not rely on the `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback, as this can be vulnerable to client-side spoofing attacks. `}</p>
    </Callout>
    <MDXCodeBlock type='server' title='/user_account/session/get' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`const request: UserAccountSessionGetRequest = {
  public_token: 'profile-sandbox-b0e2c4ee-a763-4df5-bfe9-46a46bce992d',
};
try {
  const response = await client.userAccountSessionGet(request);
} catch (error) {
  // handle error
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/user_account/session/get \\
-H 'Content-Type: application/json' \\
-d '{
  "public_token": "profile-sandbox-b0e2c4ee-a763-4df5-bfe9-46a46bce992d",
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>
}'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`request = Plaid::UserAccountSessionGetRequest.new(
  {
    public_token: 'profile-sandbox-b0e2c4ee-a763-4df5-bfe9-46a46bce992d'
  }
)
response = client.user_account_session_get(request)
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`
UserAccountSessionGetRequest request = new UserAccountSessionGetRequest()
  .publicToken("profile-sandbox-b0e2c4ee-a763-4df5-bfe9-46a46bce992d");

Response<UserAccountSessionGetResponse> response = client()
  .userAccountSessionGet(request)
  .execute();
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`request = UserAccountSessionGetRequest(
    public_token='profile-sandbox-b0e2c4ee-a763-4df5-bfe9-46a46bce992d'
)
response = client.user_account_session_get(request)
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`request := plaid.NewUserAccountSessionGetRequest(
  "profile-sandbox-b0e2c4ee-a763-4df5-bfe9-46a46bce992d",
)

response, _, err := client.PlaidApi.
  UserAccountSessionGet(ctx).
  UserAccountSessionGetRequest(*request).
  Execute()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`import (
    user "github.com/plaid/plaid-go/v49/user"
)

request := &user.UserAccountSessionGetRequest{PublicToken: "profile-sandbox-b0e2c4ee-a763-4df5-bfe9-46a46bce992d"}

response, err := client.User.Account.GetSession(ctx, request)
`}</code></pre>
    </MDXCodeBlock>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Layer sample response",
        "#Layer": true,
        "sample": true,
        "response": true
      }}>{`
{
  "identity": {
    "phone_number": "+14155550015",
    "name": {
      "first_name": "Leslie",
      "last_name": "Knope"
    },
    "address": {
      "street": "123 Main St.",
      "street2": "Apt 123",
      "city": "Pawnee",
      "region": "Indiana",
      "postal_code": "46001",
      "country": "US"
    },
    "date_of_birth": "1979-01-01",
    "ssn": "987654321",
    "ssn_last_4": "4321"
  },
  "identity_edit_history": {
    "name": {
      "edits_current": 0,
      "edits_1d": 0,
      "edits_30d": 1,
      "edits_365d": 1,
      "edits_all_time": 1
    },
    "address": {
      "edits_current": 1,
      "edits_1d": 1,
      "edits_30d": 2,
      "edits_365d": 2,
      "edits_all_time": 2
    },
    "email": {
      "edits_current": 0,
      "edits_1d": 0,
      "edits_30d": 0,
      "edits_365d": 0,
      "edits_all_time": 0
    },
    "date_of_birth": {
      "edits_current": 0,
      "edits_1d": 0,
      "edits_30d": 0,
      "edits_365d": 0,
      "edits_all_time": 0
    },
    "official_document": {
      "ssn": {
        "edits_current": 0,
        "edits_1d": 0,
        "edits_30d": 0,
        "edits_365d": 0,
        "edits_all_time": 0
      }
    }
  },
  "items": [
    {
      "item_id": "<external_item_id>",
      "access_token": "access-token-<UUID>"
    }
  ],
  "request_id": "j0LkqT9OPdVwjwh"
}
`}</code></pre>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "get-plaid-api-keys-and-complete-application-and-company-profile",
        level: 4,
        title: "Get Plaid API keys and complete application and company profile",
      },
{
        id: "install-and-initialize-plaid-libraries",
        level: 4,
        title: "Install and initialize Plaid libraries",
      },
{
        id: "launch-link",
        level: 4,
        title: "Launch Link",
      },
{
        id: "submit-the-users-phone-number",
        level: 4,
        title: "Submit the user's phone number",
      },
{
        id: "submit-the-users-date-of-birth",
        level: 4,
        title: "Submit the user's date of birth",
      },
{
        id: "open-layer-ui-on-the-layer_ready-event",
        level: 4,
        title: "Open Layer UI on the LAYER_READY event",
      },
{
        id: "get-the-public-token-from-the-onsuccess-callback",
        level: 4,
        title: "Get the public token from the onSuccess callback",
      },
{
        id: "get-user-account-data",
        level: 4,
        title: "Get user account data",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
